import React, { useContext, useState, useEffect } from 'react';
import {
    Container,
    Navbar,
    NavbarBrand,
    Nav,
    NavItem,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    NavbarToggler,
    Collapse,
    Badge
} from 'reactstrap';
import {
    Tooltip,
    CircularProgress
} from '@mui/material';
import { get } from './hooks/apiFetch';
import { useTheme } from './hooks/useTheme';
import { Link } from 'react-router-dom';
import { AuthContext } from './Auth/AuthContext';
import {
    AccountIcon,
    StarsGroupIcon,
    DarkStarsGroupIcon,
    AiAvailableIcon,
    AiUnavailableIcon,
    AiStDeferredIcon,
    AiStFreeIcon,
    AiStQueueIcon,
    AiStEmptyIcon,
    aiLogoIcon,
    darkAiLogoIcon,
    AccountWhiteIcon,
    BellIcon,
    BellWhiteIcon,
    ThemeIcon,
    ThemeWhiteIcon
} from '../assets/icons';
import './NavMenu.css';

const NavMenu = () => {
    const authContext = useContext(AuthContext);
    const { isAuthenticated, user } = authContext;
    const { theme, toggleTheme } = useTheme();
    const [collapsed, setCollapsed] = useState(true);
    const [serverHealth, setServerHealth] = useState('checking');
    const [numberOfTask, setNumberOfTask] = useState(0);
    const [notificationCount, setNotificationCount] = useState(0);
    const currentAiLogoIcon = theme === 'light' ? aiLogoIcon : darkAiLogoIcon;

    const checkServerHealth = async () => {
        try {
            const isAiServerAlive = await get('/api/app/aihealthcheck');

            if (isAiServerAlive) {
                setServerHealth('healthy');
            } else {
                setServerHealth('unhealthy');
            }
        } catch (error) {
            console.warn('Health check failed:', error);
            setServerHealth('unhealthy');
        }
    };

    const checkNumberOfTask = async () => {
        try {
            const count = await get('/api/app/numberofworktasks');
            setNumberOfTask(count);
        } catch (error) {
            console.warn('Ошибка в получении количества рабочих задач:', error);
        }
    };

    useEffect(() => {
        const updateData = async () => {
            await Promise.all([checkServerHealth(), checkNumberOfTask()]);
        };
        if (isAuthenticated) {
            updateData();
            const interval = setInterval(updateData, 5000);

            return () => clearInterval(interval);
        }
    }, [isAuthenticated]);

    const toggleNavbar = (e) => {
        e.stopPropagation();
        setCollapsed(!collapsed);
    };

    const handleLogout = async () => {
        try {
            await authContext.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            window.location.href = '/auth';
        }
    };

    const handleUserResults = () => {
        window.location.href = '/myresults';
    };

    const handleAdmin = () => {
        window.location.href = '/admin';
    };

    const handleNotifications = () => {
        window.location.href = '/notifications';
    };

    const handleStat = () => {
        window.location.href = '/statistics';
    };

    const handleBrandClick = (e) => {
        if (!isAuthenticated) {
            e.preventDefault();
            window.location.href = '/auth';
        }
    };

    const hasPermission = (requiredPermission) => {
        return authContext.checkPermission(requiredPermission);
    };

    const fetchNotificationCount = async () => {
        try {
            const userData = localStorage.getItem("userdata");
            if (!userData) return;
            const parsedData = JSON.parse(userData);
            const login = parsedData?.email;

            const data = await get(`/api/Notification/GetNewNotificationsCount?login=${encodeURIComponent(login)}`);
            setNotificationCount(data);
        } catch (error) {
            console.error('Error fetching notification count:', error);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchNotificationCount();

            const intervalId = setInterval(() => {
                fetchNotificationCount();
            }, 3000);

            return () => clearInterval(intervalId);
        }
    }, [isAuthenticated]);

    const renderAdminItem = () => {
        if (!hasPermission('aiferma_main_admin_access')) return null;

        return (
            <NavItem draggable={false}>
                <a href="/admin"
                    className="logout-item"
                    onClick={(e) => {
                        if (e.button !== 1) {
                            e.preventDefault();
                            handleAdmin();
                        }
                    }}
                    onAuxClick={(e) => {
                        if (e.button === 1) {
                            e.preventDefault();
                            window.open("/admin", "_blank");
                        }
                    }}>
                    Администрирование
                </a>
            </NavItem>
        );
    };

    const renderUserResultsItem = () => {
        return (
            <NavItem draggable={false}>
                <a href="/myresults"
                    className="logout-item"
                    onClick={(e) => {
                        if (e.button !== 1) {
                            e.preventDefault();
                            handleUserResults();
                        }
                    }}
                    onAuxClick={(e) => {
                        if (e.button === 1) {
                            e.preventDefault();
                            window.open("/myresults", "_blank");
                        }
                    }}>
                    Мои обработки
                </a>
            </NavItem>
        );
    };

    const renderNotificationsIcon = () => {
        return (
            <NavItem draggable={false} className="d-flex flex-row align-items-center">
                <a href="/notifications"
                    className="notification-link outline-disabled mr-3 ml-3"
                    onClick={(e) => {
                        if (e.button !== 1) {
                            e.preventDefault();
                            handleNotifications();
                        }
                    }}
                    onAuxClick={(e) => {
                        if (e.button === 1) {
                            e.preventDefault();
                            window.open("/notifications", "_blank");
                        }
                    }}
                    title="Уведомления">
                    <div className="notification-toggle position-relative d-inline-block">
                        {theme === 'light' ? <BellIcon /> : <BellWhiteIcon />}
                        {notificationCount > 0 && (
                            <Badge color="danger" className="notification-badge">
                                {notificationCount > 9 ? '9+' : notificationCount}
                            </Badge>
                        )}
                    </div>
                </a>
                <div className="divider" />
            </NavItem>
        );
    };

    const renderThemeToggle = () => {
        return (
            <NavItem draggable={false} className="d-flex flex-row align-items-center justify-content-center">
                <button
                    className="theme-toggle-btn outline-disabled mr-3 ml-3"
                    onClick={toggleTheme}
                    title={theme === 'light' ? 'Переключить на тёмную тему' : 'Переключить на светлую тему'}
                >
                    {theme === 'light' ? <ThemeIcon /> : <ThemeWhiteIcon />}
                </button>
                {isAuthenticated &&
                    <div className="divider desktop" />
                }
            </NavItem>
        );
    };

    const renderLogoutItem = () => {
        return (
            <NavItem draggable={false}>
                <a href="/"
                    className="logout-item"
                    onClick={(e) => {
                        if (e.button !== 1) {
                            e.preventDefault();
                            handleLogout();
                        }
                    }}
                    onAuxClick={(e) => {
                        if (e.button === 1) {
                            e.preventDefault();
                            handleLogout();
                        }
                    }}>
                    Выйти
                </a>
            </NavItem>
        );
    };

    const renderStarsGroupIcon = () => {
        return (
            <NavItem draggable={false} className="d-flex flex-row align-items-center">
                {theme === 'light' ? <StarsGroupIcon className="stars-group-icon ml-3 mr-3" /> : <DarkStarsGroupIcon className="stars-group-icon ml-3 mr-3" />}
                <div className="divider" />
            </NavItem>
        );
    };

    const renderHealthIndicator = () => {
        const getAiAvailableIcon = () => {
            switch (serverHealth) {
                case 'healthy': return (
                    <Tooltip title={getStatusTitle()} >
                        <AiAvailableIcon className="ai-server-icon mr-3" />
                    </Tooltip>);
                case 'unhealthy': return (
                    <Tooltip title={getStatusTitle()} >
                        <AiUnavailableIcon className="ai-server-icon mr-3" />
                    </Tooltip>);
                default: return <CircularProgress className="mr-3" color="primary" size={25} title={getStatusTitle()} />;
            }
        };

        const getStatusTitle = () => {
            switch (serverHealth) {
                case 'healthy': return 'Сервер доступен';
                case 'unhealthy': return 'Сервер недоступен';
                default: return 'Проверка соединения...';
            }
        };

        return getAiAvailableIcon();
    };

    const renderCheckNumberOfTask = () => {
        const getAiAvailableIcon = () => {
            if (serverHealth === 'unhealthy') {
                return <AiStEmptyIcon className="ai-server-icon mr-3" />;
            }
            if (numberOfTask === 0) {
                return (
                    <Tooltip title={getStatusTitle()}>
                        <AiStFreeIcon className="ai-server-icon mr-3" />
                    </Tooltip>
                );
            }

            if (numberOfTask < 4) {
                return (
                    <Tooltip title={getStatusTitle()}>
                        <AiStQueueIcon className="ai-server-icon mr-3" />
                    </Tooltip>
                );
            }

            return (
                <Tooltip title={getStatusTitle()}>
                    <AiStDeferredIcon className="ai-server-icon mr-3" />
                </Tooltip>
            );
        };

        const getStatusTitle = () => {
            return `Количество задач в обработке ${numberOfTask}`;
        };

        return getAiAvailableIcon();
    };

    const renderAIServerIndicatorsItem = () => {
        return (
            <NavItem draggable={false} className="health-indicator d-flex flex-row align-items-center">
                {renderHealthIndicator()}
                {renderCheckNumberOfTask()}
                <div className="divider" />
            </NavItem>
        );
    };

    const renderAdminDropdownItem = () => {
        if (!hasPermission('aiferma_main_admin_access')) return null;

        return (
            <>
                <DropdownItem divider />
                <DropdownItem draggable={false} tag="a"
                    href="/admin"
                    onClick={(e) => {
                        if (e.button !== 1) {
                            e.preventDefault();
                            handleAdmin();
                        }
                    }}
                    onAuxClick={(e) => {
                        if (e.button === 1) {
                            e.preventDefault();
                            window.open("/admin", "_blank");
                        }
                    }}>
                    Администрирование
                </DropdownItem>
            </>
        );
    };

    const renderStatDropdownItem = () => {
        if (!hasPermission('org_statistics_access')) return null;

        return (
            <>
                <DropdownItem divider />
                <DropdownItem draggable={false} tag="a"
                    href="/statistics"
                    onClick={(e) => {
                        if (e.button !== 1) {
                            e.preventDefault();
                            handleStat();
                        }
                    }}
                    onAuxClick={(e) => {
                        if (e.button === 1) {
                            e.preventDefault();
                            window.open("/statistics", "_blank");
                        }
                    }}>
                    Статистика
                </DropdownItem>
            </>
        );
    };

    const renderUserResultsDropdownItem = () => {
        return (
            <>
                <DropdownItem divider />
                <DropdownItem draggable={false} tag="a"
                    href="/myresults"
                    onClick={(e) => {
                        if (e.button !== 1) {
                            e.preventDefault();
                            handleUserResults();
                        }
                    }}
                    onAuxClick={(e) => {
                        if (e.button === 1) {
                            e.preventDefault();
                            window.open("/myresults", "_blank");
                        }
                    }}>
                    Мои обработки
                </DropdownItem>
            </>
        );
    };

    const renderThemeDropdownItem = () => {
        return (
            <>
                <DropdownItem divider />
                <DropdownItem onClick={toggleTheme}>
                    {theme === 'light' ? (
                        <>🌙 Тёмная тема</>
                    ) : (
                        <>☀️ Светлая тема</>
                    )}
                </DropdownItem>
            </>
        );
    };

    const renderLogoutDropdownItem = () => {
        return (
            <>
                <DropdownItem divider />
                <DropdownItem draggable={false} tag="a"
                    href="/"
                    onClick={(e) => {
                        if (e.button !== 1) {
                            e.preventDefault();
                            handleLogout();
                        }
                    }}
                    onAuxClick={(e) => {
                        if (e.button === 1) {
                            e.preventDefault();
                            handleLogout();
                        }
                    }}>
                    Выйти
                </DropdownItem>
            </>
        );
    };

    return (
        <header>
            <Navbar className="navbar-expand-sm navbar-toggleable-sm ng-white border-bottom box-shadow nav-bar-height" light>
                <Container>
                    <NavbarBrand className="navbar-brand" tag={Link} to="/" onClick={handleBrandClick}>
                        <img
                            src={currentAiLogoIcon}
                            alt="Логотип"
                            height="80"
                            className="d-inline-block align-top mr-2" />
                        <span>Интеллектуальные утилиты</span>
                        {isAuthenticated && (
                            <div className="mobile"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 1fr auto',
                                    alignItems: 'center'
                                }}>
                                <div className="icon-mobile">
                                    {renderHealthIndicator()}
                                    {renderCheckNumberOfTask()}
                                </div>

                                <div style={{ justifySelf: 'center' }}>
                                    {theme === 'light'
                                        ? <StarsGroupIcon className="stars-group-icon mobile icon-mobile stars-group-icon-mobile" />
                                        : <DarkStarsGroupIcon className="stars-group-icon mobile icon-mobile stars-group-icon-mobile" />}
                                </div>
                            </div>
                        )}
                    </NavbarBrand>

                    <NavbarToggler onClick={toggleNavbar} className="d-md-none" />

                    {isAuthenticated ? (
                        <>
                            <Collapse
                                className="collapse-menu d-sm-block d-md-none"
                                isOpen={!collapsed}>
                                <Nav navbar>
                                    <NavItem className="user-name-item">
                                        <span className="user-name-text">
                                            {user?.fullname.replace('}', '') || 'Пользователь'}
                                        </span>
                                    </NavItem>
                                    {renderAdminItem()}
                                    {renderUserResultsItem()}
                                    {renderNotificationsIcon()}
                                    {renderThemeToggle()}
                                    {renderLogoutItem()}
                                </Nav>
                            </Collapse>

                            <Nav className="ml-auto desktop-account d-none d-sm-flex" navbar>
                                {renderAIServerIndicatorsItem()}
                                {renderStarsGroupIcon()}
                                {renderNotificationsIcon()}
                                {renderThemeToggle()}
                                <UncontrolledDropdown nav inNavbar>
                                    <DropdownToggle nav className="account-toggle outline-disabled mr-3 ml-3">
                                        {theme === 'light' ? <AccountIcon /> : <AccountWhiteIcon />}
                                    </DropdownToggle>
                                    <DropdownMenu className="centered-dropdown">
                                        <DropdownItem header id="account-name">
                                            {user?.fullname.replace('}', '') || 'Пользователь'}
                                        </DropdownItem>
                                        {renderAdminDropdownItem()}
                                        {renderStatDropdownItem()}
                                        {renderUserResultsDropdownItem()}
                                        {renderThemeDropdownItem()}
                                        {renderLogoutDropdownItem()}
                                    </DropdownMenu>
                                </UncontrolledDropdown>
                            </Nav>
                        </>
                    ) : (
                        <>
                            <Collapse
                                className="collapse-menu d-sm-block d-md-none"
                                isOpen={!collapsed}>
                                <Nav navbar>
                                    {renderThemeToggle()}
                                </Nav>
                            </Collapse>
                            <Nav className="ml-auto desktop-account d-none d-sm-flex" navbar>
                                {renderThemeToggle()}
                            </Nav>
                        </>
                    )}
                </Container>
            </Navbar>
        </header>
    );
};

NavMenu.displayName = 'NavMenu';
export { NavMenu };